// 3rd party
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatExpansionPanel, MatTabGroup } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs/Rx';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';
import * as FileSaver from 'file-saver';
import * as _ from 'lodash';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';

// components
import { LayoutMainComponent } from 'app/shared/layout/layout-main/layout-main.component';
import { AppComponent } from 'app/app.component';
import { SampleListComponent } from 'app/assays/samples/sample-list/sample-list.component';

// helpers
import { Helper } from 'app/shared/helpers/helper';

// models
import { Instrument } from 'app/instruments/shared/instrument.model';
import { StoredFile } from 'app/shared/models/stored-file.model';
import { Calibration } from 'app/calibrations/shared/calibration.model';

// services
import { StoredFileService } from 'app/shared/services/stored-file.service';
import { DialogsService } from 'app/shared/services/dialog/dialog.service';
import { InstrumentService } from 'app/instruments/shared/instrument.service';
import { CalibrationService } from 'app/calibrations/shared/calibration.service';

@Component({
  selector: 'app-sample',
  templateUrl: './sample.component.html',
  styleUrls: ['./sample.component.css']
})
export class SampleComponent implements OnInit, OnDestroy {

  @ViewChild('samplePanel') samplePanel: MatExpansionPanel;
  @ViewChild('matTabGroup') matTabGroup: MatTabGroup;
  @ViewChild('pdfViewer') pdfViewer: PdfJsViewerComponent;

  attachments: any[] = new Array<any>();
  certificates: any[] = new Array<any>();
  checkedAttachments: any;
  checkedCertificates: any;
  chkAllAttachments: boolean = false;
  chkAllCertificates: boolean = false;
  isCheckingAll: boolean = false;
  isLoading = false;
  isMobile = false;
  sample: Instrument = new Instrument();
  subscribe1: Subscription;
  subscribe2: Subscription;

  constructor(
    private activeRoute: ActivatedRoute,
    public app: AppComponent,
    private calibrationService: CalibrationService,
    private dialog: DialogsService,
    public sampleListComponent: SampleListComponent,
    public instrumentService: InstrumentService,
    public mainLayout: LayoutMainComponent,
    public router: Router,
    public storedFileService: StoredFileService,
    public translate: TranslateService,
    private media: ObservableMedia
  ) { }

  ngOnInit(): void {
    this.subscribe2 = this.media.subscribe((change: MediaChange) => {
      this.isMobile = change.mqAlias === 'xs';
    });
    this.samplePanel.expanded = true;

    this.subscribe1 = this.activeRoute.params.subscribe(params => {
      let id = params['identifier'];
      if (!id) {
        return;
      }
      this.sampleListComponent.sidenavRight.open();
      this.instrumentService.get(id, null, null, null, null, null, '"IsSample_=1"', '"Status","Code","CollectDate","SampleExpirationDate","CollectPlace","CollectPlaceName","InstrumentType.Name","OrderService.Code","CurrentSector.Name","OrderService.ReceivingDate","Description"').subscribe(res => {
        this.sample = Helper.deserialize(res.text())[0];
        this.loadAttachments();
        this.loadCertificates();
      });
    });
  }

  ngOnDestroy() {
    if (this.subscribe1) {
      this.subscribe1.unsubscribe();
    }
    this.sampleListComponent.sidenavRight.close();
  }

  checkAllAttachments(e) {
    this.isCheckingAll = true;
    this.attachments.forEach((item) => {
      item.checked = e.source.checked;
    });
    this.isCheckingAll = false;
    this.checkedAttachments = this.attachments.filter(item => item.checked).length;
  }

  checkAllCertificates(e) {
    this.isCheckingAll = true;
    this.certificates.forEach((item) => {
      item.checked = e.source.checked;
    });
    this.isCheckingAll = false;
    this.checkedCertificates = this.certificates.filter(item => item.checked).length;
  }

  close() {
    this.sampleListComponent.sidenavRight.close();
    this.router.navigate([`/${this.app.currentContact.Domain}/samples/`]);
  }

  downloadAttachment() {
    this.isLoading = true;
    let lst = this.attachments.filter(at => at.checked);
    let files: any[] = new Array<any>();
    let fileName: string = '';

    lst.forEach(element => {
      (fileName = this.storedFileService.formatUserFileName(element.UserFileName));
      files.push({ identifier: element.Identifier, file: fileName ? fileName : element.SystemFileName, size: this.storedFileService.formatFileSize(element.size), systemFileName: element.SystemFileName, userFileName: element.UserFileName });
    });

    this.storedFileService.download(files)
      .subscribe(data => {
        if (data) {
          let fileName = files.length === 1 ? files[0].file : this.sample.Code;
          FileSaver.saveAs(data, fileName);
          this.isLoading = false;
        } else {
          this.isLoading = false;
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
        }
      }, err => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
      });
  }

  downloadCertificates() {
    this.isLoading = true;
    let selectedCalibrations = this.certificates.filter(d => d.checked) as Calibration[];
    let params = selectedCalibrations.map(calibration => {
      return {
        identifier: calibration.Identifier,
        tableName: 'new_calibrations'
      };
    });

    this.calibrationService.download(params)
      .subscribe(data => {
        this.isLoading = false;
        if (data) {
          FileSaver.saveAs(data, selectedCalibrations.length > 1 ? 'certificados.cal' : `${selectedCalibrations[0].Code}.cal`);
        } else {
          this.dialog.error('Message.ErrorAlert', selectedCalibrations.length > 1 ? 'Message.DownloadAssaysError' : 'Message.DownloadAssayError');
        }
      }, err => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', selectedCalibrations.length > 1 ? 'Message.DownloadAssaysError' : 'Message.DownloadAssayError');
      });
  }

  getAttachmentName(sf: StoredFile): string {
    let name = this.storedFileService.formatUserFileName(sf.UserFileName);
    return name ? name : sf.SystemFileName;
  }

  getStatusIcon(status: number) {
    return this.instrumentService.getStatusIcon(status);
  }

  getStatusDescription(status: number) {
    return this.instrumentService.getStatusDescription(status, true);
  }

  loadAttachments() {
    this.isLoading = true;

    let lstFilters = '"Instrument.Identifier_=\'' + this.sample.Identifier + '\'","IsAttach","VisibleWebview_=1"';
    let lstFields = '"SystemFileName","UserFileName","Size","Instrument.Code"';

    this.storedFileService
      .get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(values => {
        this.isLoading = false;
        this.attachments = values.json();

        if (this.attachments.length > 0) {
          this.attachments.forEach(element => {
            element.checked = false;
          });
        } else {
          this.attachments = null;
        }
      }, err => {
        this.isLoading = false;
        this.attachments = null;
      });
  }

  loadCertificates() {
    this.isLoading = true;
    let lstFilters = '"Instrument.Identifier_=\'' + this.sample.Identifier + '\'","Status_=1","IsAssay_=1"';

    this.calibrationService.get(null, null, null, null, null, null, lstFilters, null)
      .subscribe(values => {
        this.isLoading = false;
        this.certificates = values.json();
        this.certificates = _.orderBy(this.certificates, 'Date', 'desc');

        if (this.certificates.length > 0) {
          this.certificates.forEach(element => {
            element.checked = false;
          });
        } else {
          this.certificates = null;
        }
      }, err => {
        this.isLoading = false;
        this.certificates = null;
      });
  }

  onAttachmentSelecionChange(a) {
    if (!this.isCheckingAll) {

      if (this.attachments.filter(item => item.checked).length !== this.attachments.length && this.chkAllAttachments) {
        this.chkAllAttachments = false;
      }
      if (this.attachments.filter(item => item.checked).length === this.attachments.length && !this.chkAllAttachments) {
        this.chkAllAttachments = true;
      }

      this.checkedAttachments = this.attachments.filter(item => item.checked).length;
    }
  }

  onCertificateSelecionChange(a) {
    if (!this.isCheckingAll) {

      if (this.certificates.filter(item => item.checked).length !== this.certificates.length && this.chkAllCertificates) {
        this.chkAllCertificates = false;
      }
      if (this.certificates.filter(item => item.checked).length === this.certificates.length && !this.chkAllCertificates) {
        this.chkAllCertificates = true;
      }

      this.checkedCertificates = this.certificates.filter(item => item.checked).length;
    }
  }

  openAttachment() {
    this.isLoading = true;
    let lst = this.attachments.filter(at => at.checked);
    let file: any[] = new Array<any>();
    let fileName = this.storedFileService.formatUserFileName(lst[0].UserFileName);
    file.push({ identifier: lst[0].Identifier, file: fileName ? fileName : lst[0].SystemFileName, size: this.storedFileService.formatFileSize(lst[0].size), systemFileName: lst[0].SystemFileName, userFileName: lst[0].UserFileName });

    this.storedFileService.download(file)
      .subscribe(data => {
        if (data) {
          const blob = new Blob([data], { type: data.type });

          if (blob.type === 'application/pdf') {
            this.pdfViewer.pdfSrc = blob;
            this.pdfViewer.refresh();
          } else {
            const fileUrl = URL.createObjectURL(data);
            const newWindow = window.open(fileUrl);

            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              this.dialog.openPopupDialog();
            }
          }

          this.isLoading = false;
        } else {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
          this.isLoading = false;
        }
      }, () => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
      });
  }

  openCertificate(download: boolean) {
    this.isLoading = true;

    let lst = this.certificates.filter(item => item.checked);
    let identifiers = lst.map(obj => obj.Identifier);
    let lstFilters = '"Calibration.Identifier_in(\'' + identifiers.join('\',\'') + '\')"';
    let lstFields = '"SystemFileName","Calibration.Id","Calibration.Code","Calibration.Date"';
    let storedFiles: any[] = [];

    this.storedFileService.get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(values => {
        storedFiles = values.json() as StoredFile[];
        if (!storedFiles || storedFiles.length === 0) {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAssayError'); this.isLoading = false;
        } else {
          this.storedFileService.download(storedFiles as StoredFile[])
            .subscribe(data => {
              if (data) {
                const file = new Blob([data], { type: data.type });

                this.storedFileService.view(file, download, this.pdfViewer, `Ensaio ${storedFiles[0].Calibration.Code}`);

                this.isLoading = false;
              } else {
                this.dialog.error('Message.ErrorAlert', 'Message.ViewAssayError');
                this.isLoading = false;
              }
            }, () => {
              this.dialog.error('Message.ErrorAlert', 'Message.OpenAssayError');
              this.isLoading = false;
            });
        }
      }, () => {
        this.dialog.error('Message.ErrorAlert', 'Message.OpenAssayError');
        this.isLoading = false;
      });
  }

  sortData(data) {
    this.certificates = _.orderBy(this.certificates, [data.orderBy, 'Code'], [data.sortOrder, 'asc']);
  }
}



// WEBPACK FOOTER //
// ./src/app/assays/samples/sample/sample.component.ts